import React from "react";
import {
  MapPin,
  Calendar,
  Search,
  ArrowLeftRight,
  ArrowDownUp,
} from "lucide-react";

const SearchBar = ({
  searchQuery,
  onSearchChange,
  fromInput,
  toInput,
  swapHandler,
  onFromChange,
  onToChange,
  selectedDate,
  onDateChange,
  fromSuggestions,
  toSuggestions,
  onFromSuggestionSelect,
  onToSuggestionSelect,
  showFromSuggestions,
  showToSuggestions,
  setShowFromSuggestions,
  setShowToSuggestions,
  onSearch,
}) => {
  return (
    <div className="bg-white rounded-xl sm:rounded-2xl shadow-md sm:shadow-lg p-4 sm:p-6 mb-6 sm:mb-8">
      <div className="grid grid-cols-1 sm:grid-cols-12 gap-4">
        {/* From Input - Takes 3 columns on desktop */}
        <div className="relative sm:col-span-3">
          <MapPin className="absolute left-2 sm:left-3 top-2 sm:top-3 h-4 sm:h-5 w-4 sm:w-5 text-emerald-500" />
          <input
            type="text"
            placeholder="From (e.g., Jammu)"
            value={fromInput}
            onChange={(e) => onFromChange(e.target.value)}
            onFocus={() => setShowFromSuggestions(true)}
            onBlur={() => setTimeout(() => setShowFromSuggestions(false), 200)}
            className="w-full pl-8 sm:pl-10 pr-2 sm:pr-4 py-2 sm:py-3 border border-gray-200 rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 text-xs sm:text-sm"
          />
          {showFromSuggestions && fromSuggestions.length > 0 && (
            <ul className="absolute z-10 bg-white border rounded-lg sm:rounded-xl mt-1 w-full max-h-36 sm:max-h-40 overflow-y-auto shadow">
              {fromSuggestions.map((city, idx) => (
                <li
                  key={idx}
                  onMouseDown={() => onFromSuggestionSelect(city)}
                  className="px-3 sm:px-4 py-1.5 sm:py-2 hover:bg-emerald-50 cursor-pointer text-xs sm:text-sm"
                >
                  {city}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Swap Button Container - Takes 1 column on desktop */}
        <div className="relative sm:col-span-1 flex items-center justify-center">
          {/* Desktop Swap Button */}
          <button
            type="button"
            onClick={swapHandler}
            className="hidden sm:flex w-10 h-10 rounded-full bg-white border-2 border-gray-300 items-center justify-center hover:border-emerald-400 hover:shadow-md transition-all duration-200"
            aria-label="Swap locations"
          >
            <ArrowLeftRight className="w-5 h-5 text-gray-600 hover:rotate-180 transition-transform duration-200" />
          </button>

          {/* Mobile Swap Button - Positioned between inputs */}
          <button
            type="button"
            onClick={swapHandler}
            className="absolute sm:hidden right-1/2 translate-x-1/2 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full bg-white border-2 border-gray-300 flex items-center justify-center shadow-md hover:border-emerald-400 transition-all duration-200"
            aria-label="Swap locations"
          >
            <ArrowDownUp className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        {/* To Input - Takes 3 columns on desktop */}
        <div className="relative sm:col-span-3">
          <MapPin className="absolute left-2 sm:left-3 top-2 sm:top-3 h-4 sm:h-5 w-4 sm:w-5 text-orange-500" />
          <input
            type="text"
            placeholder="To (e.g., Srinagar)"
            value={toInput}
            onChange={(e) => onToChange(e.target.value)}
            onFocus={() => setShowToSuggestions(true)}
            onBlur={() => setTimeout(() => setShowToSuggestions(false), 200)}
            className="w-full pl-8 sm:pl-10 pr-2 sm:pr-4 py-2 sm:py-3 border border-gray-200 rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 text-xs sm:text-sm"
          />
          {showToSuggestions && toSuggestions.length > 0 && (
            <ul className="absolute z-10 bg-white border rounded-lg sm:rounded-xl mt-1 w-full max-h-36 sm:max-h-40 overflow-y-auto shadow">
              {toSuggestions.map((city, idx) => (
                <li
                  key={idx}
                  onMouseDown={() => onToSuggestionSelect(city)}
                  className="px-3 sm:px-4 py-1.5 sm:py-2 hover:bg-orange-50 cursor-pointer text-xs sm:text-sm"
                >
                  {city}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Date Input - Takes 2 columns on desktop */}
        <div className="relative sm:col-span-2">
          <Calendar className="absolute left-2 sm:left-3 top-2 sm:top-3 h-4 sm:h-5 w-4 sm:w-5 text-blue-500" />
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => onDateChange(e.target.value)}
            className="w-full pl-8 sm:pl-10 pr-2 sm:pr-4 py-2 sm:py-3 border border-gray-200 rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-xs sm:text-sm [color-scheme:light]"
            min={new Date().toISOString().split("T")[0]}
          />
        </div>

        {/* Search Button - Takes 3 columns on desktop */}
        <div className="sm:col-span-3">
          <button
            onClick={onSearch}
            className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-semibold py-2 sm:py-3 px-4 sm:px-6 rounded-lg sm:rounded-xl hover:from-emerald-600 hover:to-teal-700 transition-all duration-200 flex items-center justify-center text-xs sm:text-sm shadow-md hover:shadow-lg"
          >
            <Search className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
            Search Rides
          </button>
        </div>
      </div>
    </div>
  );
};

export default SearchBar;
